import { FC, useState, useCallback, Fragment, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BackTop, Button, Grid, Layout } from 'antd';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import {
  inquirySubmissionOpenState,
  screenSizeState,
  uiState,
  submitPurchaseAnalysisOpenState,
  siteState
} from 'recoil/atoms';
import { NavBar } from 'layout/NavBar';
import { Footer } from 'layout/Footer';
import { InquirySubmissionModal } from 'components/research/InquirySubmissionModal';
import { PurchaseAnalysisModal } from 'components/modals/PurchaseAnalysisModal';
import { useScopes } from 'hooks/useScopes';
import { Breadcrumbs } from '../Breadcrumbs';
import { ArrowLeftOutlined } from '@ant-design/icons';
import VersionAlert from 'components/VersionAlert';
import UserflowResourceCenterLauncher from 'components/UserflowResourceCenterLauncher';
// import { scrollOptions } from 'shared/constants';

const { useBreakpoint } = Grid;

const MainLayout: FC = () => {
  const { aclCheck } = useScopes();
  const screens = useBreakpoint();
  const [ui, setUI] = useRecoilState(uiState);
  const [inquiryModalOpen, setInquiryModalOpen] = useRecoilState(inquirySubmissionOpenState);
  const [purchaseAnalysisModalOpen, setPurchaseAnalysisModalOpen] = useRecoilState(submitPurchaseAnalysisOpenState);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [screenSize, setScreenSize] = useRecoilState(screenSizeState);
  const site = useRecoilValue(siteState);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showNewVersionAlert, setShowNewVersionAlert] = useState(false);

  const returnQuery = searchParams.get('return');

  const handleCollapse = useCallback((val: boolean) => {
    setSidebarOpen(val);
  }, []);

  // useEffect(() => {
  //   if (returnQuery) {
  //     scroll.scrollToTop(scrollOptions);
  //   }
  // }, [returnQuery]);

  const returnHandler = () => {
    const stepQuery = searchParams.get('step');

    if (returnQuery) {
      let step = '';
      if (stepQuery) {
        step = `?step=${stepQuery}`;
      }
      navigate(`${returnQuery}${step}`);
    }
  };

  /**
   * Loop through Grid breakpoints and determine the size of the screen
   * where: 1 = 'xs', 2 = 'sm', 3 = 'md' ... 6 = 'xl'
   */
  useEffect(() => {
    const size = Object.entries(screens).filter(([, on]) => on);
    const screenSize = size.length + (screens['xs'] === false ? 1 : 0);
    setScreenSize(screenSize);
  }, [screens, setScreenSize]);

  useEffect(() => {
    const importTheme = async () => {
      const themeStyles = await import(site === 'vantage' ? 'AppVantage.scss' : 'AppSmartBench.scss');
      document.body.className = themeStyles.container;
    };

    importTheme().then();
  }, [site]);

  const handleMenuClick = (event: any) => {
    setUI((prev) => ({ ...prev, menuKey: event?.key }));
  };

  const Menu = ui?.menu || Fragment;

  const handleInquiryModalCancel = () => {
    setInquiryModalOpen(false);
  };

  const handlePurchaseAnalysisModalCancel = () => {
    setPurchaseAnalysisModalOpen(false);
  };

  /**
   * Determines the title for the helmet based on different conditions.
   *
   * @returns {string} The title for the helmet.
   */
  const helmetTitle = (): string => {
    switch (true) {
      case !aclCheck(['read:smartbench_access']) && aclCheck(['read:portofino_full']):
        return 'NPI Vantage™ Pro';
      case !aclCheck(['read:smartbench_access']) && !aclCheck(['read:portofino_full']):
        return 'NPI SmartSpend™ Portal';
      case site === 'vantage':
        return 'NPI Vantage™ Pro';
      case site === 'smartbench':
        return 'NPI SmartBench™';
      default:
        return 'NPI Digital';
    }
  };

  return (
    <>
      <UserflowResourceCenterLauncher />
      <InquirySubmissionModal
        open={inquiryModalOpen}
        onCancel={handleInquiryModalCancel}
        onSave={handleInquiryModalCancel}
        title={'Ask an NPI Analyst'}
      />
      <PurchaseAnalysisModal
        open={purchaseAnalysisModalOpen}
        onCancel={handlePurchaseAnalysisModalCancel}
        onSave={handlePurchaseAnalysisModalCancel}
        title={'Submit a Purchase for Analysis'}
      />
      <Layout className="min-h-screen">
        <Helmet>
          <meta charSet="utf-8" />
          <title>{helmetTitle()}</title>
        </Helmet>
        <BackTop visibilityHeight={200} style={{ height: 70, right: 70 }} />

        {returnQuery && (
          <div className="w-full h-[50px] px-3 fixed top-0 left-0 right-0 bg-[#0070ff] flex items-center z-[100]">
            <Button
              type="text"
              className="text-white text-[16px] font-bold"
              icon={<ArrowLeftOutlined />}
              onClick={returnHandler}
            >
              Back to previous screen
            </Button>
          </div>
        )}

        <VersionAlert onVersionCompare={setShowNewVersionAlert} />
        <Layout.Header
          style={{
            position: 'sticky',
            top: showNewVersionAlert ? 38 : 0,
            zIndex: 100,
            width: '100%',
            paddingLeft: screenSize < 3 ? 4 : undefined,
            background: site === 'vantage' ? '#152d51' : '#11bfca'
          }}
          className={returnQuery ? 'mt-[50px]' : ''}
        >
          <NavBar />
        </Layout.Header>
        <Layout>
          <Layout.Content
            className={`${showNewVersionAlert ? 'pt-16' : 'pt-6'} bg-page ${screenSize > 4 ? 'px-[25px]' : ''}`}
          >
            <Breadcrumbs breadcrumbs={ui?.breadcrumbs || []} />
            <Layout
              className={`bg-transparent main-layout pt-3 pb-5 mb-10 m-auto ${
                screenSize > 5 ? 'max-w-[95%]' : 'max-w-[98%]'
              }`}
            >
              {ui?.menu ? (
                <Layout.Sider
                  breakpoint="lg"
                  collapsible
                  defaultCollapsed={false}
                  collapsed={sidebarOpen}
                  onCollapse={handleCollapse}
                >
                  {<Menu onClick={handleMenuClick} />}
                </Layout.Sider>
              ) : null}
              <Layout.Content className="min-h-[280px] py-0" style={{ paddingLeft: ui?.menu ? 36 : 0 }}>
                <Outlet />
              </Layout.Content>
            </Layout>
          </Layout.Content>
        </Layout>

        {site == 'vantage' && <Footer />}
      </Layout>
    </>
  );
};

export default MainLayout;
